/**
 * ลานซ้อมหุ่นไม้ (the training yard): five wooden dummies in the new Yanyu where a move can be tried at no risk. A
 * dummy is a monster (so every skill, hit number and reaction works on it as on a foe) that never dies, never moves
 * and never strikes back, and gives no experience, gold, loot or quest kill. It stands to be hit; its health refills
 * after `DUMMY.refillAfter` s without a blow. Data only; the rules are `systems/dummies.ts`.
 *
 * Three kinds: หุ่นไม้ (plain), หุ่นตั้งการ์ด (always guarding: only a break move (ท่าทำลาย) opens it, and it
 * shows the guard-break), หุ่นเกราะเหล็ก (high defence).
 */
import type { MonsterDef } from './monsters';
import type { ZoneId } from './zones';

/** The rules' numbers. */
export const DUMMY = {
  /** Seconds without a blow before the dummy's health is full again. */
  refillAfter: 3,
  /** Health of a dummy: far more than any blow, so it never falls (the bar barely moves). */
  hp: 1_000_000,
  /** The readout: damage is summed over the last N seconds; it clears this long after the last blow. */
  window: 10,
  clearAfter: 5,
} as const;

export interface DummyDef {
  /** The monster def (below). */
  readonly monster: string;
  readonly x: number;
  readonly y: number;
}

export interface DummyYard {
  readonly zone: ZoneId;
  readonly name: string;
  /** The yard's floor [x0, x1, y0, y1) (metres), a flat open court on the east bank, west of the road to the ring. */
  readonly rect: readonly [number, number, number, number];
  readonly dummies: readonly DummyDef[];
}

export const DUMMY_YARDS: readonly DummyYard[] = [
  {
    zone: 'yanyu2',
    name: 'ลานซ้อมหุ่นไม้',
    rect: [416, 440, 280, 294],
    dummies: [
      { monster: 'dummy-wood', x: 420, y: 287 },
      { monster: 'dummy-wood', x: 424.5, y: 287 },
      { monster: 'dummy-wood', x: 429, y: 287 },
      { monster: 'dummy-guard', x: 433.5, y: 287 },
      { monster: 'dummy-iron', x: 438, y: 287 },
    ],
  },
];

export const dummyYardIn = (zone: string): DummyYard | null => DUMMY_YARDS.find((y) => y.zone === zone) ?? null;

const dummy = (id: string, name: string, def: number): MonsterDef => ({
  id,
  name,
  level: 1,
  hp: DUMMY.hp,
  atk: 0,
  def,
  speed: 0,
  aggro: 0,
  leash: 0,
  decisionRate: 99,
  attack: 'wolf-bite',
  canBlock: false,
  reward: { cultivation: 0, gold: [0, 0] },
  respawn: null,
  sprite: 'dummy',
  scale: 1,
});

/** The dummies' monster defs (spread into data/monsters.ts). */
export const DUMMY_MONSTERS: readonly MonsterDef[] = [
  dummy('dummy-wood', 'หุ่นไม้', 6),
  dummy('dummy-guard', 'หุ่นตั้งการ์ด', 6),
  dummy('dummy-iron', 'หุ่นเกราะเหล็ก', 90),
];

/** Dummies that stand always guarding. */
export const GUARDING_DUMMIES: ReadonlySet<string> = new Set(['dummy-guard']);

export const isDummyMonster = (id: string | undefined): boolean => !!id && id.startsWith('dummy-');
